// recentAccountsStore — the opt-in "Continue as…" account chooser (login
// convenience). Stores only **identifiers** (the email/username string
// the user typed) — never a password or session token (the secret lives in the
// browser's password manager + an HttpOnly cookie, ADR-003). A per-device
// display preference, so localStorage is the right home (CONVENTIONS §Frontend
// state — localStorage for non-travelling display prefs; this never travels via
// the URL).
//
// Opt-in by construction: nothing is remembered unless the user ticks "Remember
// me on this device". Because SSO sign-in is a FULL-PAGE redirect (the in-memory
// intent would be lost across it), the flow is two-phase:
//   1. at "Continue", if the box is ticked, `setPendingRemember(identifier)`
//      stashes the intent in sessionStorage (survives the IdP round-trip);
//   2. once the app loads authenticated, a single post-auth effect
//      (`AppSideEffects`) calls `consumePendingRemember()` → `remember()`.
// So only accounts the user actually signed into are kept — no typo pollution.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const MAX_ACCOUNTS = 5;
const PENDING_KEY = 'ems-remember-pending';

const sameId = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

interface RecentAccountsState {
  /** Most-recent-first list of remembered identifiers. */
  accounts: string[];
  /** Move an identifier to the front (de-duped case-insensitively); cap at MAX. */
  remember: (identifier: string) => void;
  /** Drop one identifier (the per-row ×). */
  remove: (identifier: string) => void;
  /** Forget every account on this device. */
  clear: () => void;
}

export const useRecentAccountsStore = create<RecentAccountsState>()(
  persist(
    (set) => ({
      accounts: [],
      remember: (identifier) =>
        set((s) => {
          const id = identifier.trim();
          if (!id) return s;
          const without = s.accounts.filter((a) => !sameId(a, id));
          return { accounts: [id, ...without].slice(0, MAX_ACCOUNTS) };
        }),
      remove: (identifier) => set((s) => ({ accounts: s.accounts.filter((a) => !sameId(a, identifier)) })),
      clear: () => set({ accounts: [] }),
    }),
    { name: 'ems-recent-accounts' },
  ),
);

// ── pending intent (sessionStorage — survives the SSO full-page redirect) ──

/** Stash "remember this identifier on successful sign-in" before any auth path
 *  (password or the SSO redirect). sessionStorage is per-tab + per-origin, so it
 *  persists across the IdP round-trip and clears when the tab closes. */
export function setPendingRemember(identifier: string): void {
  try {
    sessionStorage.setItem(PENDING_KEY, identifier.trim());
  } catch {
    // private mode / storage disabled — convenience feature degrades silently.
  }
}

/** Read + clear the pending intent. Returns the identifier to remember, or null.
 *  One-shot: safe to call from a post-auth effect that may run more than once. */
export function consumePendingRemember(): string | null {
  try {
    const v = sessionStorage.getItem(PENDING_KEY);
    if (v) sessionStorage.removeItem(PENDING_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}
